/**
 * Dataset athlete detail — the "should I pursue this athlete" page
 * (agent epic PR 3).
 *
 * Renders the FULL enriched record (founder feedback 2026-08-12: the real
 * dataset is much richer than identity + socials): NIL valuation,
 * three-service rankings + consensus, sponsor history, contact
 * enrichment with completeness score, school + compliance contact, HS
 * athletic-dept contacts, interests, research notes, age status, and the
 * current-status line (which flags gone-pro athletes an agent should NOT
 * pursue).
 *
 * Gated the same two-layer way as the browse page: `useFeature` picks the
 * render, the server's `requireFeature` on `agency.getDatasetAthlete`
 * stays authoritative. Import snapshots the record into the agency's
 * pipeline; from there the invite happens on the Roster page where the
 * agent confirms the athlete's real email.
 */

import { Button, Card, CardContent, CardHeader, CardTitle, toast } from '@app/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { RouteErrorComponent } from '../../../../components/route-error';
import { api } from '../../../../lib/api';
import { isFeatureGateError } from '../../../../lib/feature-gate-client';
import {
  divisionLabel,
  formatMoneyCompact,
  formatReach,
  sportLabel,
} from '../../../../lib/format-reach';
import { useFeature } from '../../../../lib/use-feature';

export const Route = createFileRoute('/_authenticated/agency/recruit/$foundryId')({
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: RecruitDetailPage,
});

function UpsellCard() {
  return (
    <Card className="max-w-xl">
      <CardContent className="py-10 text-center space-y-3">
        <p className="font-medium">The recruiting dataset is an Agent plan feature.</p>
        <Button asChild>
          <Link to="/pricing" search={{ limit: 'recruitingDataset' }}>
            See Agent plans
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function RecruitDetailPage() {
  const gate = useFeature('recruitingDataset');
  if (!gate.loading && !gate.allowed) {
    return <UpsellCard />;
  }
  return <RecruitDetail />;
}

function RecruitDetail() {
  const { foundryId } = Route.useParams();
  const queryClient = useQueryClient();

  const { data: mine } = useSuspenseQuery(api.agency.myAgencies.queryOptions());
  const orgId = mine.agencies[0].id;

  const { data, error, isLoading } = useQuery(
    api.agency.getDatasetAthlete.queryOptions({ input: { foundryId, orgId } }),
  );

  const importMutation = useMutation({
    ...api.agency.importProspect.mutationOptions(),
    onSuccess: () => {
      toast.success('Imported to your pipeline — invite them from the Roster page.');
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey?.[0]) && q.queryKey[0][0] === 'agency',
      });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Import failed'),
  });

  if (error && isFeatureGateError(error)) return <UpsellCard />;
  if (error) throw error;
  if (isLoading || !data) {
    return <div className="animate-pulse h-64 max-w-2xl bg-muted rounded" />;
  }

  const { athlete, existingProspectId } = data;
  const socials = [
    { label: 'Instagram', handle: athlete.instagramHandle, followers: athlete.instagramFollowers },
    { label: 'TikTok', handle: athlete.tiktokHandle, followers: athlete.tiktokFollowers },
    { label: 'X (Twitter)', handle: athlete.twitterHandle, followers: athlete.twitterFollowers },
    { label: 'YouTube', handle: athlete.youtubeHandle, followers: athlete.youtubeFollowers },
  ].filter((s) => s.handle || s.followers);

  const rankings = [
    { label: '247Sports', value: athlete.rank247 },
    { label: 'ESPN', value: athlete.rankEspn },
    { label: 'On3/Rivals', value: athlete.rankOn3 },
  ].filter((r) => r.value);

  const contacts = [
    { label: 'Email', value: athlete.email },
    { label: 'Phone', value: athlete.phone },
    { label: 'Business / NIL contact', value: athlete.businessContact },
    { label: 'Website', value: athlete.website, href: athlete.website },
    { label: 'LinkedIn', value: athlete.linkedin, href: athlete.linkedin },
  ].filter((c) => c.value);

  const schoolFacts = [
    { label: 'School', value: athlete.school },
    { label: 'Current school', value: athlete.currentSchool },
    { label: 'Commitment', value: athlete.commitment },
    { label: 'Compliance contact', value: athlete.schoolContactEmail },
  ].filter((f) => f.value);

  const hsContacts = [
    { label: 'Contact', value: athlete.hsAthleticContact },
    { label: 'Phone', value: athlete.hsAthleticPhone },
    { label: 'Email', value: athlete.hsAthleticEmail },
  ].filter((c) => c.value);

  // The dataset flags athletes who turned pro / left college — the most
  // important line on the page for a recruiting decision.
  const inactive =
    !!athlete.currentStatus && !/^active/i.test(athlete.currentStatus.trim());

  return (
    <div className="max-w-2xl space-y-4">
      <Link to="/agency/recruit" className="text-sm text-muted-foreground hover:underline">
        ← Back to Recruit
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {athlete.photoUrl && (
            <img
              src={athlete.photoUrl}
              alt={athlete.name}
              className="h-16 w-16 rounded-full object-cover bg-muted shrink-0"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold">{athlete.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {[sportLabel(athlete.sport), athlete.position, athlete.school]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {athlete.rankingConsensus && (
              <p className="text-sm text-amber-700 mt-0.5">{athlete.rankingConsensus}</p>
            )}
          </div>
        </div>
        {existingProspectId ? (
          <Button asChild variant="outline">
            <Link to="/agency/roster">On your pipeline ✓</Link>
          </Button>
        ) : (
          <Button
            disabled={importMutation.isPending}
            onClick={() => importMutation.mutate({ orgId, foundryId })}
          >
            {importMutation.isPending ? 'Importing…' : 'Import to pipeline'}
          </Button>
        )}
      </div>

      {athlete.currentStatus && (
        <div
          className={
            inactive
              ? 'rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900'
              : 'rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900'
          }
        >
          {athlete.currentStatus}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Fact label="Class" value={athlete.classYear ? `Class of ${athlete.classYear}` : null} />
            <Fact label="Division" value={divisionLabel(athlete.division)} />
            <Fact label="School state" value={athlete.state} />
            <Fact label="Hometown" value={athlete.hometown} />
            <Fact label="High school" value={athlete.highSchool} />
            <Fact label="Age status" value={athlete.ageStatus} />
            <Fact label="NIL valuation (On3 est.)" value={formatMoneyCompact(athlete.nilValuation)} />
            <Fact label="Total reach" value={formatReach(athlete.socialFollowers)} />
            <Fact
              label="Contact completeness"
              value={
                athlete.contactCompleteness === null
                  ? null
                  : `${Math.round(athlete.contactCompleteness * 100)}%`
              }
            />
          </dl>
          {athlete.profileUrl && (
            <a
              href={athlete.profileUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-primary hover:underline mt-3 inline-block"
            >
              Recruiting profile ↗
            </a>
          )}
        </CardContent>
      </Card>

      {rankings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Rankings</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {rankings.map((r) => (
                  <tr key={r.label} className="border-t first:border-t-0">
                    <td className="px-4 py-2.5 font-medium w-36">{r.label}</td>
                    <td className="px-4 py-2.5">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {athlete.sponsors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Sponsors {athlete.sponsorCount !== null && `(${athlete.sponsorCount})`}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {athlete.sponsors.map((s) => (
              <span
                key={s.name}
                className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs"
              >
                <span className="font-medium">{s.name}</span>
                {s.industry && <span className="text-muted-foreground">{s.industry}</span>}
              </span>
            ))}
          </CardContent>
        </Card>
      )}

      {socials.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Social platforms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {socials.map((s) => (
                  <tr key={s.label} className="border-t first:border-t-0">
                    <td className="px-4 py-2.5 font-medium">{s.label}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {s.handle ? `@${s.handle}` : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">{formatReach(s.followers)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {contacts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contact & links</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.label} className="border-t first:border-t-0">
                    <td className="px-4 py-2.5 font-medium w-44">{c.label}</td>
                    <td className="px-4 py-2.5 break-all">
                      {c.href ? (
                        <a
                          href={c.href}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline"
                        >
                          {c.value}
                        </a>
                      ) : (
                        c.value
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {schoolFacts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">School & recruiting</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {schoolFacts.map((f) => (
                  <tr key={f.label} className="border-t first:border-t-0">
                    <td className="px-4 py-2.5 font-medium w-44">{f.label}</td>
                    <td className="px-4 py-2.5">{f.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {athlete.schoolWebsite && (
              <a
                href={athlete.schoolWebsite}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary hover:underline px-4 py-2.5 inline-block"
              >
                School website ↗
              </a>
            )}
          </CardContent>
        </Card>
      )}

      {hsContacts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">High-school athletic department</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {hsContacts.map((c) => (
                  <tr key={c.label} className="border-t first:border-t-0">
                    <td className="px-4 py-2.5 font-medium w-44">{c.label}</td>
                    <td className="px-4 py-2.5">{c.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {(athlete.interests || athlete.researchNotes) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Interests & research notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {athlete.interests && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Interests</p>
                <p>{athlete.interests}</p>
              </div>
            )}
            {athlete.researchNotes && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Research notes</p>
                <p>{athlete.researchNotes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        Importing snapshots this full record onto your pipeline. When you're ready, invite them
        from the Roster page — you'll confirm their real email first, and their NilBoard profile
        arrives prefilled from this data.
      </p>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}
